import { DEFAULT_STATUSES } from '../../FlowchartView/TaskNode/constants';
import { inheritTagDefinitionsFromParent } from './nestTaskHelpers';

/**
 * Handle nesting tasks into another task's nested campaign.
 * Called when user drags a node onto another node and confirms in the modal.
 */
export function runNestTasksAction({
  nestAction,
  tasks,
  nestedSprints,
  localSprint,
  setLocalSprint,
  onAddSprint,
  onUpdateSprint,
  recordAction,
  onTrashSprint,
}) {
  const { targetTaskId, draggedIds, strategy, dependencyInfo } = nestAction;

  const targetTask = tasks.find((t) => t.id === targetTaskId);
  if (!targetTask) {
    console.error('Target task not found:', targetTaskId);
    return;
  }

  let tasksToMove = draggedIds;

  if (strategy === 'chain') {
    const allIds = new Set(draggedIds);
    dependencyInfo.upstreamIds?.forEach((id) => allIds.add(id));
    dependencyInfo.downstreamIds?.forEach((id) => allIds.add(id));
    tasksToMove = Array.from(allIds);
  }

  const movingTasks = tasks.filter((t) => tasksToMove.includes(t.id));
  if (movingTasks.length === 0) {
    console.error('No tasks found to move');
    return;
  }

  const originalMovingTasks = movingTasks.map((t) => ({ ...t }));
  const originalTasks = tasks.map((t) => ({ ...t }));

  let nestedSprintId = targetTask.nestedSprintId;
  let nestedSprint = nestedSprintId ? nestedSprints.find((ns) => ns.id === nestedSprintId) || null : null;

  const isNewNestedSprint = !nestedSprintId || !nestedSprint;
  const originalNestedSprintTasks = nestedSprint ? [...(nestedSprint.tasks || [])] : null;

  if (!nestedSprint) {
    nestedSprintId = `nested-sprint-${Date.now()}`;
    const sprintName =
      targetTask.name && targetTask.name.length > 35
        ? `${targetTask.name.slice(0, 35)}...`
        : targetTask.name || 'Untitled';

    const inheritedTagDefs = inheritTagDefinitionsFromParent(localSprint);
    nestedSprint = {
      id: nestedSprintId,
      name: sprintName,
      ownerId: localSprint.ownerId,
      ownerName: localSprint.ownerName,
      status: 'active',
      progress: 0,
      createdAt: new Date().toISOString(),
      parentTaskId: targetTaskId,
      parentSprintId: localSprint.id,
      tasks: [],
      categories: inheritedTagDefs.length ? inheritedTagDefs : [],
      ...(inheritedTagDefs.length && localSprint.tags?.length
        ? { tags: inheritedTagDefs.map((t) => ({ ...t })) }
        : {}),
      statuses: localSprint.statuses || DEFAULT_STATUSES,
    };
  }

  const finalNestedSprintId = nestedSprintId;

  const anchorTask = movingTasks.reduce((anchor, task) => {
    const taskX = task.x ?? 0;
    const taskY = task.y ?? 0;
    const anchorX = anchor.x ?? 0;
    const anchorY = anchor.y ?? 0;

    if (taskY < anchorY || (taskY === anchorY && taskX < anchorX)) {
      return task;
    }
    return anchor;
  }, movingTasks[0]);

  const anchorX = anchorTask.x ?? 0;
  const anchorY = anchorTask.y ?? 0;

  const existingTasks = nestedSprint.tasks || [];
  let destAnchorX = 100;
  let destAnchorY = 100;

  if (existingTasks.length > 0) {
    const NODE_WIDTH = 288;
    let maxRightEdge = 100;

    for (const task of existingTasks) {
      const taskX = task.x ?? 0;
      const taskWidth = task.width || NODE_WIDTH;
      const rightEdge = taskX + taskWidth;
      if (rightEdge > maxRightEdge) {
        maxRightEdge = rightEdge;
      }
    }

    const GAP = 40;
    destAnchorX = maxRightEdge + GAP;
    destAnchorY = 100;
  }

  const preparedTasks = movingTasks.map((task) => {
    const offsetX = (task.x ?? 0) - anchorX;
    const offsetY = (task.y ?? 0) - anchorY;

    const newTask = {
      ...task,
      x: destAnchorX + offsetX,
      y: destAnchorY + offsetY,
    };

    if (strategy === 'break') {
      newTask.dependsOn = [];
    } else if (strategy === 'patch') {
      const movingSet = new Set(tasksToMove);
      newTask.dependsOn = (Array.isArray(task.dependsOn) ? task.dependsOn : []).filter((dep) => {
        const depId = typeof dep === 'string' ? dep : dep?.taskId;
        return movingSet.has(depId);
      });
    }

    return newTask;
  });

  const updatedNestedSprint = {
    ...nestedSprint,
    tasks: [...(nestedSprint.tasks || []), ...preparedTasks],
    categories: nestedSprint.categories || [],
  };

  const movingTaskNestedSprintIds = movingTasks.filter((t) => t.nestedSprintId).map((t) => t.nestedSprintId);

  const originalChildSprintParents = {};
  movingTaskNestedSprintIds.forEach((childSprintId) => {
    const childSprint = nestedSprints.find((ns) => ns.id === childSprintId);
    if (childSprint) {
      originalChildSprintParents[childSprintId] = childSprint.parentSprintId;
    }
  });

  if (movingTaskNestedSprintIds.length > 0 && onUpdateSprint) {
    movingTaskNestedSprintIds.forEach((childSprintId) => {
      const childSprint = nestedSprints.find((ns) => ns.id === childSprintId);
      if (childSprint) {
        onUpdateSprint({
          ...childSprint,
          tasks: childSprint.tasks || [],
          categories: childSprint.categories || [],
          parentSprintId: finalNestedSprintId,
        });
      }
    });
  }

  let patchedCurrentTasks = tasks;
  if (strategy === 'patch') {
    const movingSet = new Set(tasksToMove);

    patchedCurrentTasks = tasks.map((task) => {
      if (movingSet.has(task.id)) return task;

      const deps = Array.isArray(task.dependsOn) ? task.dependsOn : [];
      let hasMovingDep = false;
      const patchedDeps = [];

      deps.forEach((dep) => {
        const depId = typeof dep === 'string' ? dep : dep?.taskId;
        if (movingSet.has(depId)) {
          hasMovingDep = true;
          const movingTask = tasks.find((t) => t.id === depId);
          (Array.isArray(movingTask?.dependsOn) ? movingTask.dependsOn : []).forEach((upstreamDep) => {
            const upstreamId = typeof upstreamDep === 'string' ? upstreamDep : upstreamDep?.taskId;
            if (!movingSet.has(upstreamId)) {
              patchedDeps.push(upstreamDep);
            }
          });
        } else {
          patchedDeps.push(dep);
        }
      });

      if (hasMovingDep) {
        return { ...task, dependsOn: patchedDeps };
      }
      return task;
    });
  }

  const remainingTasks = patchedCurrentTasks
    .filter((t) => !tasksToMove.includes(t.id))
    .map((t) => {
      if (t.id === targetTaskId && isNewNestedSprint) {
        return { ...t, nestedSprintId: finalNestedSprintId };
      }
      return t;
    });

  const updatedParentSprint = localSprint
    ? { ...localSprint, tasks: remainingTasks }
    : null;

  setLocalSprint((prev) => {
    if (!prev) return prev;
    return {
      ...prev,
      tasks: remainingTasks,
    };
  });

  if (updatedParentSprint && onUpdateSprint) {
    onUpdateSprint(updatedParentSprint);
  }

  if (isNewNestedSprint) {
    if (onAddSprint) {
      onAddSprint(updatedNestedSprint);
    }
  } else if (onUpdateSprint) {
    onUpdateSprint(updatedNestedSprint);
  }

  if (recordAction) {
    const taskCount = originalMovingTasks.length;
    const taskNames = originalMovingTasks.slice(0, 2).map((t) => t.name || 'Untitled');
    const description = taskCount === 1 ? `Nest: "${taskNames[0]}"` : `Nest ${taskCount} missions`;

    recordAction({
      type: 'NEST_TASKS',
      description,
      undo: () => {
        setLocalSprint((prev) => {
          if (!prev) return prev;
          const restoredTasks = originalTasks.map((origTask) => {
            const wasMoving = tasksToMove.includes(origTask.id);
            if (wasMoving) {
              return { ...origTask };
            }
            return { ...origTask };
          });

          return {
            ...prev,
            tasks: restoredTasks,
          };
        });

        if (isNewNestedSprint && onTrashSprint) {
          onTrashSprint(finalNestedSprintId);
        } else if (!isNewNestedSprint && onUpdateSprint) {
          const currentNestedSprint = nestedSprints.find((ns) => ns.id === finalNestedSprintId);
          if (currentNestedSprint && originalNestedSprintTasks !== null) {
            onUpdateSprint({
              ...currentNestedSprint,
              tasks: originalNestedSprintTasks,
              categories: currentNestedSprint.categories || [],
            });
          }
        }

        if (Object.keys(originalChildSprintParents).length > 0 && onUpdateSprint) {
          Object.entries(originalChildSprintParents).forEach(([childSprintId, originalParentId]) => {
            const childSprint = nestedSprints.find((ns) => ns.id === childSprintId);
            if (childSprint) {
              onUpdateSprint({
                ...childSprint,
                tasks: childSprint.tasks || [],
                categories: childSprint.categories || [],
                parentSprintId: originalParentId,
              });
            }
          });
        }
      },
      redo: () => {
        setLocalSprint((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            tasks: remainingTasks,
          };
        });

        if (isNewNestedSprint) {
          if (onAddSprint) {
            onAddSprint(updatedNestedSprint);
          }
        } else if (onUpdateSprint) {
          onUpdateSprint(updatedNestedSprint);
        }

        if (movingTaskNestedSprintIds.length > 0 && onUpdateSprint) {
          movingTaskNestedSprintIds.forEach((childSprintId) => {
            const childSprint = nestedSprints.find((ns) => ns.id === childSprintId);
            if (childSprint) {
              onUpdateSprint({
                ...childSprint,
                tasks: childSprint.tasks || [],
                categories: childSprint.categories || [],
                parentSprintId: finalNestedSprintId,
              });
            }
          });
        }
      },
    });
  }
}
